import type { ApiAuthType } from '../services/apiConnectorService';

/**
 * How a connector signs in — the answer the one sign-in block holds (item 80),
 * and what each answer stores. Pure: no React, so the payloads are pinned by
 * `tests/unit/connector_library/signin_choice.test.mjs`.
 *
 *   mode     who signs in (a list's connector: all five; an agent's own: none / per_agent)
 *   format   where the key goes — an API: bearer · header · query · basic;
 *            an MCP server: bearer (the Authorization header) · header
 *   keyName  the header's (or the address parameter's) name, for header / query
 */

export type SignInMode = 'none' | 'shared' | 'per_agent' | 'per_person_oauth' | 'per_person_key';
export type KeyFormat = 'bearer' | 'header' | 'query' | 'basic';
export type SignInScope = 'library' | 'agent';

export interface SignInValue {
  mode: SignInMode;
  format: KeyFormat;
  keyName: string;
  value: string;
  username: string;
  password: string;
}

export const LIBRARY_MODES: SignInMode[] = ['none', 'shared', 'per_agent', 'per_person_oauth', 'per_person_key'];
export const AGENT_MODES: SignInMode[] = ['none', 'per_agent'];
export const API_FORMATS: KeyFormat[] = ['bearer', 'header', 'query', 'basic'];
export const MCP_FORMATS: KeyFormat[] = ['bearer', 'header'];
const AUTHORIZATION_HEADER = 'Authorization';
export const USUAL_NAME = { header: 'X-Api-Key', query: 'api_key' } as const;

export const NO_SIGN_IN: SignInValue = { mode: 'none', format: 'bearer', keyName: '', value: '', username: '', password: '' };

/** The modes that use a key — the ones the key format is asked for. */
export const usesKey = (mode: SignInMode): boolean =>
  mode === 'shared' || mode === 'per_agent' || mode === 'per_person_key';

/** Whether the key itself is entered in the block (everyone else's is entered by whoever owns it). */
export const entersKey = (scope: SignInScope, mode: SignInMode): boolean =>
  scope === 'library' ? mode === 'shared' : mode === 'per_agent';

/** The per-person sign-in an agent's connector takes from the list (each person's own account or key),
    or null. Only while it is attached to the list: a detached connector carries its own key. */
export function personSignIn(library: { state?: string | null; signin_mode?: SignInMode | null } | null | undefined): SignInMode | null {
  const mode = library?.signin_mode;
  const attached = library?.state === 'following' || library?.state === 'customized';
  return attached && (mode === 'per_person_key' || mode === 'per_person_oauth') ? mode : null;
}

/** Header names are case-insensitive (RFC 9110). */
const isAuthorization = (name: string): boolean => name.trim().toLowerCase() === AUTHORIZATION_HEADER.toLowerCase();

export const isNamedKey = (format: KeyFormat): format is 'header' | 'query' => format === 'header' || format === 'query';

export function keyTyped(v: SignInValue): boolean {
  return v.format === 'basic' ? !!v.username.trim() && !!v.password : !!v.value.trim();
}

/** The name a header / address parameter starts from when its format is chosen. */
export function nameFor(kind: 'api' | 'mcp', format: KeyFormat, current: string): string {
  if (!isNamedKey(format)) return current;
  const usual = !current || isAuthorization(current) || Object.values(USUAL_NAME).includes(current as never);
  if (!usual) return current;
  return kind === 'mcp' ? USUAL_NAME.header : USUAL_NAME[format];
}

/** An API's stored settings, as the block's answer. */
export function apiSignIn(mode: SignInMode, authType: ApiAuthType | undefined | null,
  meta: { in?: 'header' | 'query'; name?: string } | null | undefined): SignInValue {
  const format: KeyFormat = authType === 'api_key' ? (meta?.in === 'query' ? 'query' : 'header')
    : authType === 'basic' ? 'basic' : 'bearer';
  return { ...NO_SIGN_IN, mode, format, keyName: meta?.name || (isNamedKey(format) ? USUAL_NAME[format] : '') };
}

/** An MCP server's stored header, as the block's answer. */
export function mcpSignIn(mode: SignInMode, headerName: string | null | undefined): SignInValue {
  const named = !!headerName && !isAuthorization(headerName);
  // The Authorization header keeps its name as stored (any case), so a save never rewrites it.
  return { ...NO_SIGN_IN, mode, format: named ? 'header' : 'bearer', keyName: headerName || '' };
}

/** A list entry's stored sign-in, as the block's answer. */
export function storedSignIn(entry: {
  kind: 'api' | 'mcp'; signin_mode?: SignInMode | null; auth_type?: ApiAuthType | null;
  auth_meta?: { in?: 'header' | 'query'; name?: string } | null; auth_header_name?: string | null;
}): SignInValue {
  const mode = entry.signin_mode ?? 'none';
  return entry.kind === 'api' ? apiSignIn(mode, entry.auth_type, entry.auth_meta) : mcpSignIn(mode, entry.auth_header_name);
}

/** What an API's answer stores: `auth_type` and `auth`, in the shapes the API forms always sent. */
export function apiAuthOf(v: SignInValue, withKey: boolean): { auth_type: ApiAuthType; auth: Record<string, string> } {
  if (!usesKey(v.mode)) return { auth_type: 'none', auth: {} };
  if (isNamedKey(v.format)) {
    return { auth_type: 'api_key', auth: { in: v.format, name: v.keyName.trim(), ...(withKey ? { value: v.value.trim() } : {}) } };
  }
  if (v.format === 'basic') {
    return { auth_type: 'basic', auth: withKey ? { username: v.username.trim(), password: v.password } : {} };
  }
  return { auth_type: 'bearer', auth: withKey ? { value: v.value.trim() } : {} };
}

/** The header an MCP server's key goes in. */
export const mcpHeaderOf = (v: SignInValue): string =>
  v.format === 'header' || isAuthorization(v.keyName) ? v.keyName.trim() : AUTHORIZATION_HEADER;

/** The header (with the typed key) an MCP server is sent, or undefined when no key is typed for a choice with one. */
export function mcpHeaders(v: SignInValue): Record<string, string> | undefined {
  return usesKey(v.mode) && v.value.trim() ? { [mcpHeaderOf(v)]: v.value.trim() } : undefined;
}

/** A list entry's Sign-in save: the Settings save's own fields for that kind
    (`PUT …/signin-settings` turns them into the stored fields). */
export function libraryKeyFields(kind: 'api' | 'mcp', v: SignInValue): Record<string, unknown> {
  if (!usesKey(v.mode)) return {};
  const withKey = v.mode === 'shared' && keyTyped(v);
  if (kind === 'api') return apiAuthOf(v, withKey);
  return withKey ? { headers: mcpHeaders(v) } : { auth_header_name: mcpHeaderOf(v) };
}

/** Whether the answer is complete enough to save (a stored key counts as entered when `keyStored`). */
export function signInComplete(scope: SignInScope, v: SignInValue, keyStored: boolean): boolean {
  if (!usesKey(v.mode)) return true;
  if (isNamedKey(v.format) && !v.keyName.trim()) return false;
  return !entersKey(scope, v.mode) || keyTyped(v) || keyStored;
}

/** The part of an answer a save changes (the mode, and where the key goes). */
export const signInShape = (v: SignInValue): string =>
  JSON.stringify([v.mode, usesKey(v.mode) ? v.format : null, usesKey(v.mode) ? v.keyName.trim() : null]);
